/**
 * TCX-8 (K78-C) — client helpers for Training Companion monitoring consent.
 *
 * - `checkTrainingMonitoringConsent` reads the durable consent row (shop members
 *   may SELECT via RLS). CONSERVATIVE: any error / absent / pre-migration state
 *   returns false ("not confirmed") so the UI prompts rather than silently
 *   assuming consent. It is NOT the authority — the server mint/ingest gates are.
 * - `grantTrainingMonitoringConsent` records opt-in via the secure edge function
 *   `log-training-monitoring-consent` (RLS forbids a direct client write). Never
 *   persists consent in localStorage.
 *
 * Runtime-active only after migration 20260711120100 applies +
 * log-training-monitoring-consent deploys.
 */
import { supabase } from '@/integrations/supabase/client';
import { emitCompanionConsentRevoked } from '@/lib/trainingCompanion/companionConsentRevoked';

export interface ConsentScope {
  technicianProfileId: string;
  shopId: string;
}

/**
 * CURRENT monitoring-consent policy version — MUST mirror the server
 * (`current_training_monitoring_policy_version()` /
 * `CURRENT_TRAINING_MONITORING_POLICY_VERSION`). A stored consent whose
 * `policy_version` differs is STALE: the server gate denies it and the UI must
 * prompt for re-consent. This constant is for DISPLAY/UX only — it is never the
 * authority (the server always re-checks).
 */
export const CURRENT_TRAINING_MONITORING_POLICY_VERSION = 'v1';

export interface ConsentState {
  /** True only when an un-revoked opt_in at the CURRENT policy version exists. */
  isActive: boolean;
  /** An un-revoked opt_in exists but under an older policy version. */
  isStale: boolean;
  status: 'opt_in' | 'opt_out' | 'none';
  policyVersion: string | null;
  grantedAt: string | null;
  revokedAt: string | null;
}

export async function checkTrainingMonitoringConsent(scope: ConsentScope): Promise<boolean> {
  if (!scope.technicianProfileId || !scope.shopId) return false;
  try {
    const { data, error } = await supabase
      .from('training_monitoring_consents')
      .select('id')
      .eq('technician_profile_id', scope.technicianProfileId)
      .eq('shop_id', scope.shopId)
      .eq('status', 'opt_in')
      .is('revoked_at', null)
      // Stale-version consent does not count as active — mirrors the server gate.
      .eq('policy_version', CURRENT_TRAINING_MONITORING_POLICY_VERSION)
      .maybeSingle();
    if (error) return false; // pre-apply / RLS / transient → not confirmed (prompt)
    return !!data;
  } catch {
    return false;
  }
}

/**
 * Full consent state for a management surface (view + revoke/re-consent). Reads
 * the latest un-revoked opt_in (if any) via RLS. Conservative on error: reports
 * `none` so the UI offers to grant rather than implying a false active state.
 */
export async function getTrainingMonitoringConsentState(scope: ConsentScope): Promise<ConsentState> {
  const empty: ConsentState = {
    isActive: false, isStale: false, status: 'none', policyVersion: null, grantedAt: null, revokedAt: null,
  };
  if (!scope.technicianProfileId || !scope.shopId) return empty;
  try {
    const { data, error } = await supabase
      .from('training_monitoring_consents')
      .select('status, policy_version, granted_at, revoked_at')
      .eq('technician_profile_id', scope.technicianProfileId)
      .eq('shop_id', scope.shopId)
      .eq('status', 'opt_in')
      .is('revoked_at', null)
      .maybeSingle();
    if (error || !data) return empty;
    const row = data as { status: string; policy_version: string; granted_at: string | null; revoked_at: string | null };
    const current = row.policy_version === CURRENT_TRAINING_MONITORING_POLICY_VERSION;
    return {
      isActive: current,
      isStale: !current,
      status: 'opt_in',
      policyVersion: row.policy_version,
      grantedAt: row.granted_at,
      revokedAt: row.revoked_at,
    };
  } catch {
    return empty;
  }
}

export async function revokeTrainingMonitoringConsent(
  scope: ConsentScope,
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data, error } = await supabase.functions.invoke('log-training-monitoring-consent', {
      body: {
        technician_profile_id: scope.technicianProfileId,
        shop_id: scope.shopId,
        status: 'opt_out',
      },
    });
    if (error) return { success: false, error: error.message ?? 'Failed to revoke consent' };
    if (data && (data as { success?: boolean }).success) {
      // CORE-AI Slice 2 — the DURABLE revocation has landed; only now do we tell
      // any Companion on this device to stop observing.
      //
      // Order is the whole point. If the RPC had failed we would fall through to
      // the error branches below and say NOTHING to the extension: never claim
      // locally that consent is gone when the database still says it is active.
      //
      // Fire-and-forget, and deliberately not awaited or checked: if the
      // Companion is missing, asleep, or on another device, this revocation has
      // still succeeded and the next evidence POST is refused with 403, which
      // runs the same terminal teardown. A local bridge failure must never make
      // durable revocation fail.
      emitCompanionConsentRevoked();
      return { success: true };
    }
    return { success: false, error: 'Consent was not revoked' };
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : 'Failed to revoke consent' };
  }
}

export async function grantTrainingMonitoringConsent(
  scope: ConsentScope,
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data, error } = await supabase.functions.invoke('log-training-monitoring-consent', {
      body: {
        technician_profile_id: scope.technicianProfileId,
        shop_id: scope.shopId,
        status: 'opt_in',
      },
    });
    if (error) return { success: false, error: error.message ?? 'Failed to record consent' };
    if (data && (data as { success?: boolean }).success) return { success: true };
    return { success: false, error: 'Consent was not recorded' };
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : 'Failed to record consent' };
  }
}
